import { useId } from 'react';
import { cn } from '@/lib/utils';

// =====================================================================
// Toggle — sage-when-on switch primitive. Used by pricing.highlighted
// and (in future sub-phases) contact.formEnabled. Label sits left of
// the switch; whole row is clickable.
// =====================================================================

interface ToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** A save is in flight: the switch stays FOCUSABLE (aria-disabled + aria-busy,
   *  never native `disabled`, which would drop keyboard focus mid-interaction)
   *  and ignores input until the caller clears it. */
  pending?: boolean;
  /** id of an element that describes the switch (aria-describedby). */
  describedBy?: string;
  /** Row-layout override for a specific call site (e.g. get-found-ready's
   *  wider gap-6). Appended last so twMerge lets it win over the default
   *  gap-3 without changing every other Toggle call site. */
  className?: string;
}

export function Toggle({ label, description, checked, onChange, disabled, pending, describedBy, className }: ToggleProps) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex items-center justify-between gap-3',
        // T12 fix round 1 (F1): the hit area lives HERE, not at the call
        // site. The whole row is the label, so min-h-6 gives every Toggle the
        // 24 px minimum and pointer-coarse:min-h-11 the 44 pt touch minimum —
        // the same pair T11/A7 had hand-written on the notification rows
        // before they became switches. Nine call sites, one rule.
        'min-h-6 pointer-coarse:min-h-11',
        disabled ? 'cursor-not-allowed opacity-60' : pending ? 'cursor-progress opacity-80' : 'cursor-pointer',
        'select-none',
        className,
      )}
    >
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-foreground">{label}</span>
        {description ? (
          <span className="block text-xs text-text-tertiary mt-0.5">{description}</span>
        ) : null}
      </span>
      <span className="relative shrink-0">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={checked}
          aria-checked={checked}
          disabled={disabled}
          aria-disabled={pending || undefined}
          aria-busy={pending || undefined}
          aria-describedby={describedBy}
          onChange={(e) => {
            if (pending) return; // controlled: React keeps `checked` at the confirmed value
            onChange(e.target.checked);
          }}
          className="peer sr-only"
        />
        <span
          aria-hidden
          className={cn(
            'inline-block h-5 w-9 rounded-full',
            'transition-colors duration-[var(--duration-fast)]',
            'border border-border',
            // Unchecked track was bg-surface-sunken (near-white) — barely
            // distinguishable from the surrounding card background, so an
            // off switch read as "no control here" rather than "off"
            // (design audit A10). border-strong was the first fix and still
            // fell under 3:1 against the card, so the white knob had no
            // reliable edge (B6); text-tertiary is the darkest neutral we
            // already ship and clears it. The checked track's brand-500 fill
            // was already fine (non-text, large-area use is allowed).
            checked ? 'bg-brand-500 border-brand-600' : 'bg-text-tertiary',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-brand-500',
            'peer-focus-visible:outline-offset-2',
          )}
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none',
            'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-card shadow-sm',
            'transition-transform duration-[var(--duration-fast)]',
            checked && 'translate-x-4',
          )}
        />
      </span>
    </label>
  );
}
